import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, RefreshCw, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ProductsPanel from "./admin/ProductsPanel";
import PrintfulPanel from "./admin/PrintfulPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { user, isAdmin, isAuthenticated, loading: authLoading, login, logout, token, authHeader } = useAuth();
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // order being viewed
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState("orders"); // orders | products | printful

  const fetchData = async () => {
    const [ordersRes, statsRes] = await Promise.all([
      axios.get(`${API}/admin/orders`, { headers: authHeader }),
      axios.get(`${API}/admin/stats`, { headers: authHeader }),
    ]);
    setOrders(ordersRes.data || []);
    setStats(statsRes.data);
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData().catch((e) => {
        console.warn("[admin] data fetch failed:", e);
        toast.error("Failed to load admin data.");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) {
      setErr(res.error || "Login failed.");
      return;
    }
    if (res.user?.role !== "admin") {
      setErr("That account isn't an admin.");
      logout();
      return;
    }
    // useEffect will fire fetchData on isAdmin change
  };

  const handleLogout = () => {
    logout();
    setOrders([]);
    setStats(null);
    nav("/");
  };

  const resync = async (sessionId) => {
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API}/admin/orders/${sessionId}/sync`,
        {},
        { headers: authHeader },
      );
      setSelected(res.data);
      await fetchData();
      toast.success("Order resynced from Stripe.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resync failed.");
    } finally {
      setSyncing(false);
    }
  };

  if (authLoading) {
    return (
      <div className="px-4 md:px-8 py-20 min-h-[70vh] flex items-center justify-center" data-testid="admin-loading">
        <p className="font-condensed uppercase tracking-widest">Checking session…</p>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="px-4 md:px-8 py-20 min-h-[70vh] flex items-center justify-center" data-testid="admin-login">
        <form onSubmit={handleLogin} className="border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard p-8 w-full max-w-md">
          <p className="font-condensed text-sm uppercase tracking-widest text-[#D95D39]">// Admin only</p>
          <h1 className="font-display text-4xl uppercase mt-2">Sign in.</h1>
          <input
            type="email"
            placeholder="Admin email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-duds mt-6 bg-[#FFFDF5]"
            data-testid="admin-email-input"
            autoFocus
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-duds mt-3 bg-[#FFFDF5]"
            data-testid="admin-password-input"
            required
          />
          {err && <p className="text-[#D95D39] mt-3 font-condensed text-sm uppercase tracking-widest" data-testid="admin-error">{err}</p>}
          <button type="submit" disabled={loading} className="btn-duds w-full mt-6" data-testid="admin-login-button">
            {loading ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-10" data-testid="admin-dashboard">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="font-condensed text-sm uppercase tracking-widest text-[#D95D39]">// Admin</p>
          <h1 className="font-display text-5xl uppercase mt-1">
            {tab === "orders" ? "Orders" : tab === "products" ? "Products" : "Printful"}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {tab === "orders" && (
            <button
              onClick={() => fetchData()}
              className="border-2 border-[#1C1914] px-3 py-2 font-condensed text-xs uppercase tracking-widest hover:bg-[#FDE047]"
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={14} className="inline mr-1" /> Refresh
            </button>
          )}
          <button
            onClick={handleLogout}
            className="font-condensed text-sm uppercase tracking-widest hover:text-[#D95D39]"
            data-testid="admin-logout-button"
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b-4 border-[#1C1914]" data-testid="admin-tabs">
        <TabButton active={tab === "orders"} onClick={() => setTab("orders")} testId="tab-orders">
          Orders {stats ? `(${stats.total_orders})` : ""}
        </TabButton>
        <TabButton active={tab === "products"} onClick={() => setTab("products")} testId="tab-products">
          Products {stats?.products_count != null ? `(${stats.products_count})` : ""}
        </TabButton>
        <TabButton active={tab === "printful"} onClick={() => setTab("printful")} testId="tab-printful">
          Printful
        </TabButton>
      </div>

      {tab === "orders" && (
        <>
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
              <Stat label="Total orders" value={stats.total_orders} />
              <Stat label="Paid orders" value={stats.paid_orders} />
              <Stat label="Revenue" value={`A$${stats.revenue_usd.toFixed(2)}`} />
              <Stat label="Newsletter" value={stats.newsletter_signups} />
            </div>
          )}

          <div className="border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard overflow-x-auto" data-testid="admin-orders-table">
        <table className="w-full text-sm">
          <thead className="bg-[#1C1914] text-[#FFFDF5] font-condensed uppercase tracking-widest text-xs">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Items</th>
              <th className="text-left px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Payment</th>
              <th className="text-left px-4 py-3">Fulfillment</th>
            </tr>
          </thead>
          <tbody className="font-body">
            {orders.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-[#1C1914]/60">
                  No orders yet. As expected.
                </td>
              </tr>
            )}
            {orders.map((o) => (
              <tr
                key={o.id}
                onClick={() => setSelected(o)}
                className="border-t border-[#1C1914]/15 cursor-pointer hover:bg-[#FDE047]/30"
                data-testid={`admin-order-${o.id}`}
              >
                <td className="px-4 py-3 whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 truncate max-w-[180px]">{o.email || "—"}</td>
                <td className="px-4 py-3 text-xs">
                  {(o.lines || []).map((l, i) => (
                    <div key={i}>
                      {l.quantity}× {l.product_name}{l.color ? ` — ${l.color}` : ""} <span className="text-[#1C1914]/60">({l.size})</span>
                    </div>
                  ))}
                </td>
                <td className="px-4 py-3 font-display text-base">A${o.total_usd?.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <Tag value={o.payment_status} good="paid" />
                </td>
                <td className="px-4 py-3">
                  <Tag value={o.fulfillment_status} good="fulfilled" warn="queued" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </>
      )}

      {tab === "products" && <ProductsPanel token={token} />}

      {tab === "printful" && <PrintfulPanel token={token} />}

      {selected && (
        <OrderDetailDrawer
          order={selected}
          onClose={() => setSelected(null)}
          onResync={resync}
          syncing={syncing}
        />
      )}
    </div>
  );
};

const TabButton = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 font-condensed uppercase tracking-widest text-sm border-x-2 border-t-2 border-[#1C1914] -mb-1 ${
      active
        ? "bg-[#1C1914] text-[#FFFDF5]"
        : "bg-[#FFFDF5] text-[#1C1914] hover:bg-[#FDE047]"
    }`}
  >
    {children}
  </button>
);

const Stat = ({ label, value }) => (
  <div className="border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard-sm p-4">
    <div className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70">{label}</div>
    <div className="font-display text-3xl md:text-4xl mt-1">{value}</div>
  </div>
);

const Tag = ({ value, good = "", warn = "" }) => {
  const v = (value || "—").toString();
  const isGood = v === good;
  const isWarn = v === warn;
  const cls = isGood
    ? "bg-[#4A5D23] text-[#FFFDF5]"
    : isWarn
    ? "bg-[#FDE047] text-[#1C1914]"
    : "bg-[#1C1914]/10 text-[#1C1914]";
  return (
    <span className={`inline-block px-2 py-1 font-condensed text-xs uppercase tracking-widest ${cls}`}>
      {v}
    </span>
  );
};

const Row = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-dashed border-[#1C1914]/20" data-testid={`order-row-${label.replace(/\s+/g, "-").toLowerCase()}`}>
    <span className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 flex-shrink-0">{label}</span>
    <span className={`text-right text-sm ${mono ? "font-mono break-all" : "font-body"}`}>{value || "—"}</span>
  </div>
);

const OrderDetailDrawer = ({ order, onClose, onResync, syncing }) => {
  const [copied, setCopied] = useState(false);
  const addr = order.shipping_address || {};
  const hasAddress = addr.line1 || addr.city || addr.country;
  const stripeUrl = order.session_id
    ? `https://dashboard.stripe.com/payments/${order.stripe_payment_intent || ""}`
    : null;
  const lines = order.lines || [];

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-[#1C1914]/60 z-50"
        onClick={onClose}
        data-testid="order-detail-backdrop"
      />
      <aside
        className="fixed top-0 right-0 h-full w-full sm:w-[520px] bg-[#FFFDF5] z-50 border-l-4 border-[#1C1914] overflow-y-auto"
        data-testid="order-detail-drawer"
      >
        <div className="sticky top-0 bg-[#FFFDF5] flex items-center justify-between px-5 py-4 border-b-4 border-[#1C1914]">
          <div>
            <p className="font-condensed text-xs uppercase tracking-widest text-[#D95D39]">// Order</p>
            <h2 className="font-display text-2xl uppercase leading-none mt-1">
              A${order.total_usd?.toFixed(2)} · <Tag value={order.payment_status} good="paid" />
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="border-2 border-[#1C1914] p-2 hover:bg-[#FDE047]"
            data-testid="order-detail-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-6">
          {/* Line items */}
          <div data-testid="order-detail-lines">
            <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-2">
              Items ({lines.reduce((s, l) => s + l.quantity, 0)})
            </p>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between border-2 border-[#1C1914] bg-[#FFFDF5] px-3 py-2"
                  data-testid={`order-detail-line-${i}`}
                >
                  <div>
                    <div className="font-display text-base uppercase leading-tight">{l.product_name}</div>
                    <div className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70">
                      {l.color ? `${l.color} · ` : ""}Size {l.size} · Qty {l.quantity}
                    </div>
                  </div>
                  <div className="font-display text-base">
                    A${(l.unit_price_usd * l.quantity).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="bg-[#1C1914] text-[#FFFDF5] p-4">
            <div className="flex justify-between text-sm py-1">
              <span>Subtotal</span><span>A${(order.subtotal_usd || 0).toFixed(2)}</span>
            </div>
            {order.discount_usd > 0 && (
              <div className="flex justify-between text-sm py-1 text-[#FDE047]">
                <span>{order.promo_label || "Discount"}</span>
                <span>-A${order.discount_usd.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm py-1">
              <span>{(order.shipping_usd || 0) === 0 ? "Free shipping" : "Shipping"}</span>
              <span>A${(order.shipping_usd || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-display text-xl pt-2 border-t border-[#FFFDF5]/30 mt-2">
              <span>TOTAL</span><span>A${(order.total_usd || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Customer */}
          <div>
            <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-2">Customer</p>
            <Row label="Email" value={order.email} />
            <Row label="Name" value={order.customer_name || order.shipping_name} />
            <Row label="Phone" value={order.customer_phone || order.shipping_phone} />
          </div>

          {/* Shipping address */}
          <div>
            <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-2">Ship to</p>
            {hasAddress ? (
              <div className="border-2 border-[#1C1914] p-3 font-body text-sm leading-relaxed" data-testid="order-detail-address">
                {order.shipping_name && <div>{order.shipping_name}</div>}
                {addr.line1 && <div>{addr.line1}</div>}
                {addr.line2 && <div>{addr.line2}</div>}
                <div>
                  {[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}
                </div>
                <div>{addr.country}</div>
              </div>
            ) : (
              <p className="text-sm text-[#1C1914]/60 italic">
                No shipping address on file — buyer hasn't completed checkout yet, or paid before address collection was enabled.
              </p>
            )}
          </div>

          {/* Status meta */}
          <div>
            <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-2">Status</p>
            <Row label="Payment" value={<Tag value={order.payment_status} good="paid" />} />
            <Row label="Fulfilment" value={<Tag value={order.fulfillment_status} good="fulfilled" warn="queued" />} />
            <Row label="Supplier" value={order.supplier} />
            <Row label="Email sent" value={order.email_sent ? "Yes" : "No"} />
            {order.email_error && <Row label="Email error" value={order.email_error} />}
            <Row label="Promo" value={order.promo_code || "—"} />
            <Row label="Created" value={new Date(order.created_at).toLocaleString()} />
            <Row label="Updated" value={new Date(order.updated_at).toLocaleString()} />
          </div>

          {/* Printful fulfilment */}
          {(order.supplier === "printful" || order.printful_order_id || order.printful_error) && (
            <div data-testid="order-detail-printful">
              <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-2">Printful</p>
              <Row label="Status" value={<Tag value={order.printful_status || order.fulfillment_status} good="fulfilled" warn="queued" />} />
              {order.printful_order_id && <Row label="Order ID" value={order.printful_order_id} mono />}
              {order.tracking_url && (
                <Row label="Tracking" value={
                  <a href={order.tracking_url} target="_blank" rel="noreferrer" className="underline hover:text-[#D95D39]">
                    {order.carrier || "Track"}
                  </a>
                } />
              )}
              {order.tracking_number && <Row label="Tracking #" value={order.tracking_number} mono />}
              {order.printful_error && (
                <div className="mt-2 p-2 border-2 border-[#D95D39] bg-[#D95D39]/10 text-xs text-[#D95D39]">
                  <strong>Error:</strong> {order.printful_error}
                </div>
              )}
              {order.printful_dashboard_url && (
                <a
                  href={order.printful_dashboard_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-xs underline hover:text-[#D95D39]"
                >
                  Open in Printful Dashboard <ExternalLink size={12} />
                </a>
              )}
            </div>
          )}

          {/* Stripe */}
          <div>
            <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-2">Stripe</p>
            <div className="flex items-start justify-between gap-2 py-2 border-b border-dashed border-[#1C1914]/20">
              <span className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70">Session</span>
              <div className="text-right">
                <code className="text-xs font-mono break-all">{order.session_id}</code>
                <button
                  type="button"
                  onClick={() => copy(order.session_id)}
                  className="ml-1 align-middle text-[#1C1914]/60 hover:text-[#1C1914]"
                  data-testid="order-detail-copy-session"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
            {order.stripe_payment_intent && (
              <Row label="Payment Intent" value={order.stripe_payment_intent} mono />
            )}
            {stripeUrl && order.stripe_payment_intent && (
              <a
                href={stripeUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs underline hover:text-[#D95D39]"
                data-testid="order-detail-stripe-link"
              >
                Open in Stripe Dashboard <ExternalLink size={12} />
              </a>
            )}
          </div>

          {/* Resync action */}
          <button
            onClick={() => onResync(order.session_id)}
            disabled={syncing}
            className="btn-duds w-full"
            data-testid="order-detail-resync"
          >
            {syncing ? "Resyncing…" : "Resync from Stripe"}
          </button>
          <p className="text-xs text-[#1C1914]/60 -mt-3">
            Pulls latest payment status, customer email, phone, shipping address from Stripe and triggers the confirmation email if missing.
          </p>
        </div>
      </aside>
    </>
  );
};

export default AdminPage;
